import { Typography, Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import Grid from "@mui/material/Grid";
import { ProjectResponse } from "api/generatedApi";
import DialogTitleStyled from "components/shared/dialog/dialog-title-styled";
import { usePaperProps } from "components/shared/dialog/paper-props";
import { UseTreeFolderCounter } from "components/shared/tree-folder-counter/use-tree-folder-counter";
import { TreeSelector } from "components/shared/tree/selector/default";
import { ExtendedProjectFolder } from "pages/project/hooks/use-map-tree-to-flat";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { DialogBaseProps } from "shared/dialog/types";

interface Props extends DialogBaseProps {
  projectDestination: ProjectResponse;
  dataFlatlist: ExtendedProjectFolder[];
  selectedFolder?: ExtendedProjectFolder;
  onSubmit: (destinationFolderId: string, destinationProjectId: string, setAmountsToZero: boolean) => void;
  crossProjectCopy: boolean;
}

export const FolderCopy = (props: Props) => {
  const { projectDestination, dataFlatlist, selectedFolder, onSubmit, crossProjectCopy } = props;
  const { t } = useTranslation();
  const [destinationFolderId, setDestinationFolderId] = useState("");
  const [validationError, setValidationError] = useState<string | undefined>(undefined);
  const { paperProps } = usePaperProps();
  const [setAmountsToZeroStep, setSetAmountsToZeroStep] = useState(false);
  const folderSelected = (nodeId: string) => {
    setDestinationFolderId(nodeId);
  };
  const { validateAppendFolderIntoFolder } = UseTreeFolderCounter();

  const exceedMaxFolders = (thisFolder?: ExtendedProjectFolder, moveToFolder?: ExtendedProjectFolder): boolean => {
    if (!thisFolder || !moveToFolder) {
      return false;
    }
    if (validateAppendFolderIntoFolder(thisFolder, moveToFolder)) {
      setValidationError(t("folder.copy.exceedMaxSubFoldersError"));
      return false;
    }
    return true;
  };

  const handleClick = () => {
    if (crossProjectCopy) {
      setSetAmountsToZeroStep(true);
    } else onSubmitHandler(false);
  };

  const onSubmitHandler = (setAmountsToZero: boolean) => {
    const sourceFolder = dataFlatlist.find((f) => f.projectFolderId === selectedFolder?.projectFolderId);
    const destFolder = dataFlatlist.find((f) => f.projectFolderId === destinationFolderId);

    // Check if this is a same-project copy (source folder exists in destination project)
    const isSameProject = !!sourceFolder;
    if (!destFolder) {
      return;
    }

    if (!destinationFolderId || !projectDestination.projectId) {
      return;
    }

    // Only validate folder structure for same-project copies
    if (isSameProject && !exceedMaxFolders(sourceFolder, destFolder)) {
      return;
    }

    // For cross-project copies, we still need a valid destination
    if (!isSameProject && !exceedMaxFolders(selectedFolder, destFolder)) {
      return;
    }

    onSubmit(destinationFolderId, projectDestination.projectId, setAmountsToZero);
  };

  return (
    <>
      <Dialog maxWidth="sm" open={props.isOpen && !setAmountsToZeroStep} PaperProps={paperProps}>
        <DialogTitleStyled title={t("folder.copy.title")} description={dataFlatlist.find((f) => f.projectFolderId === selectedFolder?.projectFolderId) ? t("folder.copy.description") : t("folder.copy.placementDescription")} onClose={props.onClose} isOpen={props.isOpen} />
        <DialogContent>
          <Grid item xs={12} color={"error.main"} textAlign={"center"}>
            {validationError}
          </Grid>
          <TreeSelector project={projectDestination} dataFlatlist={dataFlatlist} folderSelectedProps={(nodeId: string) => folderSelected(nodeId)} />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={props.onClose}>
            {t("common.cancel")}
          </Button>
          <Button variant="contained" color="primary" onClick={handleClick}>
            {t("folder.copy.copyToPlacement")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog maxWidth="sm" open={setAmountsToZeroStep && props.isOpen} PaperProps={paperProps}>
        <DialogTitleStyled onClose={props.onClose} title={t("folder.copy.title")} description={t("folder.copy.includeAmountsDescription")} isOpen={setAmountsToZeroStep} showDivider={false} />

        <DialogContent sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>

          <Typography variant="h5" color="primary.main" sx={{ px: 5, textAlign: "center", }}>
            {t("folder.copy.setAmountsToZeroDescription")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={props.onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              onSubmitHandler(true);
            }}
          >
            {t("folder.copy.includeAmountsButtonNo")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              onSubmitHandler(false);
            }}
          >
            {t("folder.copy.includeAmountsButtonYes")}
          </Button>
        </DialogActions>
      </Dialog >
    </>
  );
};
